/**
 * DialerGamificationContext — XP / Level / Power Hour session state.
 *
 * 2026-06-04 (Fowler étape 1/5 du découpage) — Extrait du god-context
 * `DialerContext.tsx` selon la carte des dépendances
 * `docs/cartography/dialer-context-dependencies.md`.
 *
 * Responsabilité unique :
 *   "Combien de XP / quel niveau a l'agent, et est-il en Power Hour ?"
 *
 * Sources de vérité :
 *   - agentXP / agentLevel  ← `/api/gamification/profile` (fetch au mount)
 *   - powerHourActive       ← session uniquement (jamais persisté serveur)
 *   - powerHourTimer        ← countdown 1s, reset à 3600 quand off
 *   - powerHourCalls        ← incrémenté par `useDialerSessionHandlers`
 *
 * Dépendances entrantes :
 *   - useDialerAuth() → callerData.caller_email + organisationId (scope ADR-031)
 *
 * Consommateurs (lecture) :
 *   - components/dialer/PowerHourBadge.tsx (via useDialer() façade)
 *   - hooks/useDialerSessionHandlers.ts (props injection conservée)
 *
 * Pas de cycle : Auth → Gamification, et personne ne mute Auth depuis ici.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useDialerAuth } from './DialerAuthContext';

// ── Public shape ──────────────────────────────────────────────────────────────

export interface DialerGamificationContextValue {
  // XP / Level (persisté serveur)
  agentXP: number;
  agentLevel: number;
  addXP: (amount: number) => void;

  // Power Hour (session, non persisté)
  powerHourActive: boolean;
  setPowerHourActive: React.Dispatch<React.SetStateAction<boolean>>;
  powerHourTimer: number;
  setPowerHourTimer: React.Dispatch<React.SetStateAction<number>>;
  powerHourCalls: number;
  setPowerHourCalls: React.Dispatch<React.SetStateAction<number>>;
}

const DialerGamificationContext =
  createContext<DialerGamificationContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function DialerGamificationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { callerData } = useDialerAuth();
  const callerEmail = callerData?.caller_email;
  const orgId = callerData?.auth?.activeMembership?.organisationId;

  // XP / Level
  const [agentXP, setAgentXP] = useState(0);
  const [agentLevel, setAgentLevel] = useState(1);

  // Power Hour
  const [powerHourActive, setPowerHourActive] = useState(false);
  const [powerHourTimer, setPowerHourTimer] = useState(3600);
  const [powerHourCalls, setPowerHourCalls] = useState(0);

  // Fetch gamification profile au mount (ADR-031 : scope = active organisation).
  useEffect(() => {
    if (!callerEmail || !orgId) return;
    fetch(`/api/gamification/profile?agent_email=${encodeURIComponent(callerEmail)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { xp?: number; level?: number } | null) => {
        if (data) {
          setAgentXP(data.xp || 0);
          setAgentLevel(data.level || 1);
        }
      })
      .catch(() => {});
  }, [callerEmail, orgId]);

  // Power Hour countdown 1s.
  useEffect(() => {
    if (!powerHourActive) return;
    const interval = setInterval(() => {
      setPowerHourTimer((prev) => {
        if (prev <= 1) {
          setPowerHourActive(false);
          // Toast doit etre rendu par le consommateur (PowerHourBadge ou
          // useDialerSessionHandlers). Pas de side-effect UI ici pour
          // garder le contexte testable en environnement node.
          return 3600;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [powerHourActive]);

  // addXP : double les XP pendant Power Hour (GAP-2).
  const addXP = useCallback(
    (amount: number) => {
      if (!callerEmail || !orgId) return;
      const multiplier = powerHourActive ? 2 : 1;
      const finalAmount = amount * multiplier;
      fetch('/api/gamification/add-xp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_email: callerEmail,
          amount: finalAmount,
          source: powerHourActive ? 'power_hour' : 'standard',
        }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data: { xp?: number; level?: number } | null) => {
          if (data) {
            setAgentXP(data.xp || 0);
            setAgentLevel(data.level || 1);
          }
        })
        .catch(() => {});
    },
    [callerEmail, orgId, powerHourActive],
  );

  const value = useMemo<DialerGamificationContextValue>(
    () => ({
      agentXP,
      agentLevel,
      addXP,
      powerHourActive,
      setPowerHourActive,
      powerHourTimer,
      setPowerHourTimer,
      powerHourCalls,
      setPowerHourCalls,
    }),
    [agentXP, agentLevel, addXP, powerHourActive, powerHourTimer, powerHourCalls],
  );

  return (
    <DialerGamificationContext.Provider value={value}>
      {children}
    </DialerGamificationContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useDialerGamification(): DialerGamificationContextValue {
  const ctx = useContext(DialerGamificationContext);
  if (!ctx) {
    throw new Error(
      'useDialerGamification must be used within <DialerGamificationProvider>',
    );
  }
  return ctx;
}
